import type { MembershipDto } from "@bafe/api-client";
import type { OrganisationRole } from "@bafe/types";
import { accountDisplayName, accountInitials, cn } from "@bafe/ui";
import { DotsThreeIcon } from "@phosphor-icons/react";
import { Grid2X2, Home, Settings } from "lucide-react";
import { useState } from "react";

import { ROLE_LABEL } from "../lib/status.js";
import { AccountWorkspaceScroll } from "./AccountWorkspaceScroll.js";
import { Button } from "./ui/button.js";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "./ui/dropdown-menu.js";

const MENU_ROLE_LABEL: Record<OrganisationRole, string> = {
	owner: "Owner",
	admin: "Admin",
	user: "User",
	billing: "Billing",
	support_viewer: "Support viewer",
};

function workspaceInitial(organisationName: string): string {
	const trimmed = organisationName.trim();
	const first = trimmed[0];
	return first ? first.toUpperCase() : "?";
}

export type PlatformAccountMenuProps = {
	name: string;
	email: string;
	settingsHref: string;
	onSettingsClick?: () => void;
	onSignOut: () => void;
	signingOut?: boolean;
	darkMode?: boolean;
	onDarkModeChange?: (dark: boolean) => void;
	memberships: MembershipDto[];
	activeOrganisationId: string;
	onSwitchWorkspace: (organisationId: string) => void;
	onManageWorkspace: (organisationId: string) => void;
	/** Opens Platform Workspace (workspace list, users, billing). */
	onPlatformWorkspace?: () => void;
	platformWorkspaceActive?: boolean;
	onUserDashboard?: () => void;
	userDashboardActive?: boolean;
	/** Test hook: open the menu on mount. */
	defaultOpen?: boolean;
};

export function PlatformAccountMenu({
	name,
	email,
	settingsHref,
	onSettingsClick,
	onSignOut,
	signingOut = false,
	darkMode,
	onDarkModeChange,
	memberships,
	activeOrganisationId,
	onSwitchWorkspace,
	onManageWorkspace,
	onPlatformWorkspace,
	platformWorkspaceActive = false,
	onUserDashboard,
	userDashboardActive = false,
	defaultOpen = false,
}: PlatformAccountMenuProps) {
	const label = accountDisplayName(name, email);
	const initials = accountInitials(name, email);
	const showWorkspaceSwitch = memberships.length > 0;
	const [open, setOpen] = useState(defaultOpen);

	return (
		<div className="platform-account-menu relative flex w-full min-w-0 items-center gap-2">
			<span
				aria-hidden="true"
				className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-(--color-brand-50) text-xs font-semibold text-(--color-brand-700)"
			>
				{initials}
			</span>
			<div className="min-w-0 flex-1">
				<p className="truncate text-sm font-medium text-(--text-primary)">{label}</p>
				<p className="truncate text-xs text-(--text-muted)">{email}</p>
			</div>
			<DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
				{/* Anchor at the sidebar right edge (offset past account row padding). */}
				<DropdownMenuTrigger asChild>
					<button
						type="button"
						tabIndex={-1}
						aria-hidden="true"
						className="platform-account-menu-anchor pointer-events-none absolute top-0 right-[-12px] bottom-0 w-px border-0 bg-transparent p-0 opacity-0"
					/>
				</DropdownMenuTrigger>
				<button
					type="button"
					aria-label={`Account menu for ${label}`}
					aria-haspopup="menu"
					aria-expanded={open}
					onClick={() => setOpen((current) => !current)}
					className={cn(
						"relative z-[1] inline-flex size-8 shrink-0 items-center justify-center rounded-(--radius-md) border-0 bg-transparent",
						"text-(--text-muted) hover:bg-(--surface-sunken) hover:text-(--text-primary)",
						open && "bg-(--surface-sunken) text-(--text-primary)",
					)}
				>
					<DotsThreeIcon className="size-5" aria-hidden="true" />
				</button>
				<DropdownMenuContent
					side="right"
					align="end"
					sideOffset={10}
					avoidCollisions
					collisionPadding={{ top: 10, right: 10, bottom: 10, left: 10 }}
					className="platform-account-menu-content min-w-64 max-w-[min(100vw-16px,20rem)] p-0"
				>
					{showWorkspaceSwitch && (
						<>
							<DropdownMenuLabel className="platform-account-menu-section px-3 py-2 text-[11px] font-semibold tracking-wide text-(--text-muted) uppercase">
								Switch workspace
							</DropdownMenuLabel>
							<AccountWorkspaceScroll
								role="group"
								aria-label="Workspaces"
								className="max-h-[min(240px,40vh)] border-0"
							>
								{onPlatformWorkspace ? (
									<DropdownMenuItem
										className={cn(
											"platform-account-workspace-item flex cursor-pointer items-center gap-2.5 rounded-none py-0 pl-3 pr-3",
											platformWorkspaceActive && "bg-(--surface-sunken)",
										)}
										onSelect={(event) => {
											event.preventDefault();
											onPlatformWorkspace();
											setOpen(false);
										}}
									>
										<span
											aria-hidden="true"
											className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-(--surface-sunken) text-(--text-primary)"
										>
											<Grid2X2 className="size-4" aria-hidden="true" />
										</span>
										<span className="min-w-0 flex-1 py-2.5">
											<span className="flex min-w-0 items-center gap-2">
												<span className="truncate text-sm font-medium text-(--text-primary)">
													Platform Workspace
												</span>
												{platformWorkspaceActive ? (
													<span className="workspace-current-badge shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-(--color-brand-700) uppercase">
														Current
													</span>
												) : null}
											</span>
										</span>
									</DropdownMenuItem>
								) : null}
								{memberships.map((membership) => {
									const active =
										!platformWorkspaceActive &&
										activeOrganisationId.length > 0 &&
										membership.organisationId === activeOrganisationId;
									const roleLabel =
										MENU_ROLE_LABEL[membership.role] ?? ROLE_LABEL[membership.role];
									return (
										<DropdownMenuItem
											key={membership.organisationId}
											className={cn(
												"platform-account-workspace-item flex cursor-pointer items-center gap-2.5 rounded-none py-0 pl-3 pr-1",
												active && "bg-(--surface-sunken)",
											)}
											onSelect={(event) => {
												event.preventDefault();
												if (!active) {
													onSwitchWorkspace(membership.organisationId);
													setOpen(false);
												}
											}}
										>
											<span
												aria-hidden="true"
												className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-(--surface-sunken) text-sm font-semibold text-(--text-primary)"
											>
												{workspaceInitial(membership.organisationName)}
											</span>
											<span className="min-w-0 flex-1 py-2.5">
												<span className="flex min-w-0 items-center gap-2">
													<span className="truncate text-sm font-medium text-(--text-primary)">
														{membership.organisationName}
													</span>
													{active && (
														<span className="workspace-current-badge shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-(--color-brand-700) uppercase">
															Current
														</span>
													)}
												</span>
												<span className="block truncate text-xs text-(--text-muted)">
													{roleLabel}
												</span>
											</span>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className="platform-account-workspace-gear size-8 shrink-0 text-(--text-muted) hover:text-(--text-primary)"
												aria-label={`Manage ${membership.organisationName}`}
												onPointerDown={(event) => event.stopPropagation()}
												onClick={(event) => {
													event.preventDefault();
													event.stopPropagation();
													onManageWorkspace(membership.organisationId);
													setOpen(false);
												}}
											>
												<Settings className="size-4" aria-hidden="true" />
											</Button>
										</DropdownMenuItem>
									);
								})}
							</AccountWorkspaceScroll>
							<DropdownMenuSeparator className="my-0 bg-(--border-subtle)" />
						</>
					)}
					<div className="p-1">
						{onUserDashboard && !userDashboardActive ? (
							<DropdownMenuItem
								onSelect={(event) => {
									event.preventDefault();
									onUserDashboard();
									setOpen(false);
								}}
							>
								<Home className="mr-2 size-4" aria-hidden="true" />
								User dashboard
							</DropdownMenuItem>
						) : null}
						<DropdownMenuItem asChild>
							<a
								href={settingsHref}
								onClick={(event) => {
									if (
										onSettingsClick &&
										event.button === 0 &&
										!event.metaKey &&
										!event.ctrlKey &&
										!event.shiftKey &&
										!event.altKey
									) {
										event.preventDefault();
										onSettingsClick();
									}
								}}
							>
								Settings
							</a>
						</DropdownMenuItem>
						{onDarkModeChange && (
							<DropdownMenuItem
								onSelect={(event) => {
									event.preventDefault();
									onDarkModeChange(!darkMode);
								}}
								className="flex items-center justify-between gap-3"
							>
								Dark mode
								<span
									aria-hidden="true"
									className={cn(
										"relative h-[18px] w-8 shrink-0 rounded-full transition-colors",
										darkMode ? "bg-(--color-brand-600)" : "bg-(--color-ink-200)",
									)}
								>
									<span
										className={cn(
											"absolute top-[2px] left-[2px] size-3.5 rounded-full bg-white shadow-sm transition-transform",
											darkMode && "translate-x-3.5",
										)}
									/>
								</span>
							</DropdownMenuItem>
						)}
						<DropdownMenuItem
							disabled={signingOut}
							onSelect={() => onSignOut()}
							className="text-(--color-critical-700) focus:text-(--color-critical-700)"
						>
							{signingOut ? "Signing out…" : "Log out"}
						</DropdownMenuItem>
					</div>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}
